import React, { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Badge, Box, Stack, Tab, Tabs, Typography, Skeleton } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import { useQuery } from '@apollo/client'
import EmptyScreen from 'packages/ui/EmptyScreen/EmptyScreen'

import { useUser } from 'store'
import ActionModal from 'components/Modal/ActionModal'
import TreeProvider from 'packages/ui/Arborist/TreeProvider'
import { useSitesSelected, useTreeContext } from 'packages/ui/Arborist/TreeProvider'
import { checkInPerimeter } from 'packages/ui/Arborist/utils'
import { deserializeErrors } from 'apollo-client'

import { Main } from './EditStyles'
import InformationPanel from './Panels/Information'
import RolesPanel from './Panels/Roles'
import AccountSitePanel from './Panels/AccountsPerimeter'
import useSave, { buildSchema, useIsLocked } from './use-save'

import { OPERATION_FIND_CONTACT } from '../operations-gql/queries'
import { DEFAULT_CONTACT } from './constants'
import { deserializeContact } from './utils'
import { trackEvent } from 'analytics'

const Panels = ({ selectedTab, loading, isLocked, onSubmit, onGoBack, contact }) => {
  return (
    <>
      {selectedTab === 'infos' && <InformationPanel isLocked={isLocked} loading={loading} onSubmit={onSubmit} />}
      {selectedTab === 'roles' && (
        <RolesPanel
          contact={contact}
          isInternal={contact.internal}
          isLocked={isLocked}
          loading={loading}
          onSubmit={onSubmit}
          onGoBack={onGoBack ? () => onGoBack('infos') : null}
        />
      )}
      {selectedTab === 'account_sites' && (
        <AccountSitePanel
          contact={contact}
          isLocked={isLocked}
          isInternal={contact.internal}
          loading={loading}
          onSubmit={onSubmit}
          onGoBack={onGoBack ? () => onGoBack('roles') : null}
        />
      )}
    </>
  )
}

const TabsForm = ({ selectedTab, onChange, hasBadge, hasError, disabled }) => {
  const { t } = useTranslation()
  return (
    <Tabs className="justifyStart" value={selectedTab} sx={{ p: 0 }} onChange={(e, value) => onChange(value)}>
      <Tab
        component="div"
        label={
          hasError ? (
            <Badge
              color="error"
              variant="dot"
              sx={{
                '& .MuiBadge-dot': {
                  top: '-4px'
                }
              }}>
              {t('auth.detail.tabs.infos')}
            </Badge>
          ) : (
            t('auth.detail.tabs.infos')
          )
        }
        value="infos"
      />
      <Tab component="div" label={t('auth.detail.tabs.roles')} value="roles" disabled={disabled} />
      <Tab
        component="div"
        disabled={disabled}
        label={
          hasBadge ? (
            <Badge
              color="error"
              variant="dot"
              sx={{
                '& .MuiBadge-dot': {
                  top: '-4px'
                }
              }}>
              {t('auth.detail.tabs.sites')}
            </Badge>
          ) : (
            t('auth.detail.tabs.sites')
          )
        }
        value="account_sites"
      />
    </Tabs>
  )
}

const Loading = () => {
  const { sfId } = useParams()
  const { t } = useTranslation()
  return (
    <Main component="main">
      <Typography variant="h2" sx={{ color: 'grey.800' }}>
        {sfId ? `${t('auth.admin.edit.title')}` : `${t('ui_elements.form_creation')}`}
      </Typography>
      <TabsForm selectedTab={'infos'} />

      <Stack gap={3}>
        {Array.from({ length: 3 }).map((r, ri) => (
          <Stack direction="row" gap={3} key={ri}>
            {Array.from({ length: 3 }).map((c, ci) => (
              <Stack gap={1} key={ci}>
                <Skeleton variant={'text'} width={200} />
                <Skeleton variant={'rectangular'} width={260} height={32} sx={{ borderRadius: 1 }} />
              </Stack>
            ))}
          </Stack>
        ))}
        <Skeleton variant={'text'} sx={{ borderRadius: 1 }} width={200} />
      </Stack>
    </Main>
  )
}
const Edit = ({ contact }) => {
  const [selectedTab, setSelectedTab] = useState('infos')
  const { t } = useTranslation()
  const { selection } = useTreeContext()
  const { count: nbSitesSelected } = useSitesSelected({ selection })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const save = useSave()
  const validationNewContact = buildSchema({ t })
  const methods = useForm({
    defaultValues: {
      ...contact,
      roles: {
        ...contact.roles,
        admin_users_role:
          contact.roles.admin_contact && contact.roles.admin_roles
            ? 'admin_both'
            : contact.roles.readonly_contact && contact.roles.admin_contact && !contact.roles.admin_roles
              ? 'readonly'
              : 'none',
        dashboard: true
      }
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(validationNewContact)
  })
  const navigate = useNavigate()
  const sfId = methods.watch('sf_id')
  const isLocked = useIsLocked({ sfId })
  const errorMessage = useMemo(() => {
    if (!error || ['Errors::MultipleParentAccounts', 'Errors::LargePerimeterMustBeConfirmed'].includes(error)) {
      return null
    } else if (error === 'Restforce::ErrorCode::FieldCustomValidationException') {
      return t('errors.contact.FieldCustomValidationException')
    } else if (error === 'Restforce::ErrorCode::DuplicatesDetected') {
      return t('errors.contact.DuplicatesDetected')
    }
    return error
  }, [error])
  const handleSave = async values => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { admin_users_role, ...roles } = values.roles
    setLoading(true)
    setError(null)
    try {
      const result = await save({ ...values, roles: { ...roles, center_beta: true } })
      trackEvent(sfId ? 'user_updated' : 'user_created', {
        internal: Boolean(values.internal)
      })
      methods.setValue('sf_id', result.sf_id)
      setTimeout(() => navigate(sfId ? `/contacts/list/${sfId}` : '/contacts/list'), 2000)
    } catch (e) {
      setError(deserializeErrors(e))
    } finally {
      setLoading(false)
    }
  }
  const handleSubmit = async values => {
    if (!sfId) {
      if (selectedTab === 'infos') {
        setSelectedTab('roles')
      } else if (selectedTab === 'roles') {
        setSelectedTab('account_sites')
      } else {
        await handleSave(values)
      }
    } else {
      await handleSave(values)
    }
  }
  return (
    <FormProvider {...methods}>
      <input type={'hidden'} {...methods.register('internal')} />
      {['Errors::MultipleParentAccounts', 'Errors::LargePerimeterMustBeConfirmed'].includes(error) && (
        <ActionModal
          cancelLabel={t('ui_elements.cancel')}
          confirmLabel={t('ui_elements.confirm')}
          description={t(`auth.admin.edit.error_modal.${error.replaceAll('::', '')}`)}
          loading={loading}
          onClose={() => setError(null)}
          onConfirm={() => handleSubmit({ ...methods.getValues(), confirm: true })}
          title={t('auth.admin.edit.error_modal.title')}
        />
      )}
      <Main component="main">
        <Stack direction="row" gap={1}>
          <Typography variant="h2" sx={{ color: 'grey.800' }}>
            {sfId ? `${t('auth.admin.edit.title')}` : `${t('ui_elements.form_creation')}`}
          </Typography>
          <Typography variant="h2" sx={{ color: 'primary.main' }}>
            {`${contact.first_name} ${contact.last_name}`}
          </Typography>
          {(isLocked || Boolean(errorMessage)) && (
            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'center'}
              p={'0 12px'}
              ml={2}
              sx={{ background: '#FEE2E2', borderRadius: '6px' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'error.main' }}>
                {errorMessage || t('auth.admin.edit.locked')}
              </Typography>
            </Box>
          )}
        </Stack>
        <Box component={'form'}>
          <input type={'hidden'} {...methods.register('sf_id')} />
          <input type={'hidden'} {...methods.register('center_user_id')} />
          <input type={'hidden'} {...methods.register('locked')} />
          <TabsForm
            selectedTab={selectedTab}
            onChange={setSelectedTab}
            disabled={!sfId}
            hasError={Object.entries(methods.formState.errors).length > 0 && sfId}
            hasBadge={!contact.internal && nbSitesSelected === 0 && sfId}
          />
          <Box mt={2}>
            <Panels
              contact={contact}
              loading={loading}
              isLocked={isLocked}
              selectedTab={selectedTab}
              onGoBack={!sfId && setSelectedTab}
              onSubmit={methods.handleSubmit(handleSubmit)}
            />
          </Box>
        </Box>
      </Main>
    </FormProvider>
  )
}

const EditLoader = () => {
  const { t } = useTranslation()
  const { sfId } = useParams()
  const [user] = useUser()
  const { loading, error, data } = useQuery(OPERATION_FIND_CONTACT, {
    variables: {
      sf_id: sfId,
      with_accounts: true
    },
    fetchPolicy: 'no-cache',
    skip: sfId.indexOf('@') > -1
  })
  const contact = useMemo(() => {
    if (sfId.indexOf('@') > -1) {
      const roles = {
        ...DEFAULT_CONTACT.roles,
        statistic_ssid: user.admin ? true : Boolean(user.roles?.statistic_ssid),
        supervision: user.admin ? true : Boolean(user.roles?.supervision)
      }
      return {
        ...DEFAULT_CONTACT,
        roles,
        email: sfId,
      }
    } else if (!data) {
      return
    }
    return deserializeContact(data.contact)
  }, [sfId, data])

  return loading ? (
    <Loading />
  ) : error ? (
    <Main component="main">
      <EmptyScreen icon="alert-triangle" iconSize="80px" fontSize="14px">
        {t('errors.contact.load_failed')}
      </EmptyScreen>
    </Main>
  ) : (
    <TreeProvider initialSelection={checkInPerimeter({ accounts: contact.accounts })}>
      <Edit contact={contact} />
    </TreeProvider>
  )
}
export default EditLoader
